import { parseDbTimestamp } from "@repo/shared";
import type { SyncTranscriptionResponse } from "@repo/shared/types";
import { ArrowLeft, Check, FileText, Loader2, Video } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LexicalMarkdownEditor } from "@/components/ui/markdown-editor-lexical";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { TimePicker } from "@/components/ui/time-picker";
import { useConfirm } from "@/hooks/use-confirm";
import { get, patch, post } from "@/lib/api/_client";
import {
  formatLocalDateInput,
  formatLocalTimeInput,
  localDateTimeToIso,
  nowLocalTimeInput,
  todayLocalDateInput,
} from "@/lib/date-utils";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useThirdPartyFeatures } from "@/lib/hooks/use-third-party-features";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { getRelativeTime } from "@/lib/utils";
import { MEETING_TEXT, T } from "./topic-entry-editor.i18n";

export type TopicEntry = {
  id: string;
  topic: string;
  content: string;
  occurredAt: string;
  summary: string | null;
  createdBy?: { id: string; name: string };
  updatedAt: string;
  // Optional meeting room fields (used when meetingRoom prop is enabled)
  hasMeetingRoom?: boolean;
  roomDurationMinutes?: number;
  roomUrl?: string | null;
  hostRoomUrl?: string | null;
  transcription?: string | null;
  transcriptionStatus?: string | null;
  transcriptionLanguage?: string | null;
};

// Whereby-supported language codes (curated subset of the API enum we confirmed via curl).
const TRANSCRIPTION_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "no", label: "Norsk" },
  { code: "sv", label: "Svenska" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "pt", label: "Português" },
];

function appLocaleToWherebyCode(appLocale: string): string {
  const base = (appLocale.split("_")[0] || "en").toLowerCase();
  if (base === "nb" || base === "nn") return "no";
  return base;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

function toIsoDate(dateInput: string): string {
  // Calls/notes: treat user-entered date as noon-UTC of that day for stable display.
  return new Date(`${dateInput}T12:00:00Z`).toISOString();
}

export type TopicEntryEditorProps<TEntry extends TopicEntry> = {
  /** `null` creates a new entry; the first successful save turns it into an update. */
  entry: TEntry | null;
  /** Collection endpoint, e.g. `/internal/client-calls/<clientId>`. */
  endpointBase: string;
  onDelete?: (id: string) => void;
  /** Back / done — the parent returns to its list and refetches. */
  onCancel: () => void;
  /** Lower-case noun used in the delete confirmation when the topic is empty. */
  entryWord: string;
  /** Meetings: time picker, Whereby room and transcription controls. */
  meetingRoom?: boolean;
};

// The full-page editor for a call or a meeting (QL-125: the list that used to
// wrap it lives in notes-and-files-section.tsx, shared with notes and files).
export function TopicEntryEditor<TEntry extends TopicEntry>({
  entry,
  endpointBase,
  onDelete,
  onCancel,
  entryWord,
  meetingRoom = false,
}: TopicEntryEditorProps<TEntry>) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const today = todayLocalDateInput();
  const nowTime = nowLocalTimeInput();
  const mt = useT(MEETING_TEXT);
  const [topic, setTopic] = useState(entry?.topic || "");
  const [content, setContent] = useState(entry?.content || "");
  const [occurredAtDate, setOccurredAtDate] = useState(
    entry?.occurredAt ? formatLocalDateInput(entry.occurredAt) : today,
  );
  const [occurredAtTime, setOccurredAtTime] = useState(
    entry?.occurredAt && meetingRoom ? formatLocalTimeInput(entry.occurredAt) : nowTime,
  );
  const [hasMeetingRoom, setHasMeetingRoom] = useState(!!entry?.hasMeetingRoom);
  // Whereby video meetings are gated per-tenant. Assume enabled while the toggle
  // query loads (the server enforces). An existing room still shows even if off.
  const { features: tpFeatures } = useThirdPartyFeatures();
  const wherebyEnabled = tpFeatures?.wherebyEnabled ?? true;
  const [roomDurationMinutes, setRoomDurationMinutes] = useState(entry?.roomDurationMinutes ?? 120);
  const [roomUrl, setRoomUrl] = useState<string | null>(entry?.roomUrl ?? null);
  const [hostRoomUrl, setHostRoomUrl] = useState<string | null>(entry?.hostRoomUrl ?? null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const { confirm, ConfirmationDialog } = useConfirm();

  const isFutureDate = useMemo(() => {
    if (meetingRoom) {
      return new Date(`${occurredAtDate}T${occurredAtTime}:00`).getTime() > Date.now();
    }
    return new Date(`${occurredAtDate}T23:59:59Z`).getTime() > Date.now();
  }, [occurredAtDate, occurredAtTime, meetingRoom]);

  // Note: we do NOT auto-clear hasMeetingRoom when the date moves into the past.
  // Past meetings that had a room must keep that fact visible (links, transcription).
  // The disabled state below prevents creating a *new* room for a past meeting,
  // but leaves an existing one intact.

  const savedId = useRef<string | null>(entry?.id || null);
  // The save currently on the wire, if any. The autosave and a manual Save can
  // overlap while the create POST is still in flight; without this both would
  // see no saved id and create the entry twice.
  const inFlight = useRef<Promise<boolean> | null>(null);
  const initialOccurred = entry?.occurredAt ? formatLocalDateInput(entry.occurredAt) : today;
  const initialOccurredTime =
    entry?.occurredAt && meetingRoom ? formatLocalTimeInput(entry.occurredAt) : nowTime;
  const lastSaved = useRef({
    topic: entry?.topic || "",
    content: entry?.content || "",
    occurredAt: initialOccurred,
    occurredAtTime: initialOccurredTime,
    hasMeetingRoom: !!entry?.hasMeetingRoom,
    roomDurationMinutes: entry?.roomDurationMinutes ?? 120,
  });

  const hasUnsavedChanges =
    topic !== lastSaved.current.topic ||
    content !== lastSaved.current.content ||
    occurredAtDate !== lastSaved.current.occurredAt ||
    (meetingRoom && occurredAtTime !== lastSaved.current.occurredAtTime) ||
    (meetingRoom && hasMeetingRoom !== lastSaved.current.hasMeetingRoom) ||
    (meetingRoom && roomDurationMinutes !== lastSaved.current.roomDurationMinutes);

  const debouncedTopic = useDebouncedValue(topic, 10000);
  const debouncedContent = useDebouncedValue(content, 10000);
  const debouncedDate = useDebouncedValue(occurredAtDate, 10000);
  const debouncedTime = useDebouncedValue(occurredAtTime, 10000);
  const debouncedRoom = useDebouncedValue(hasMeetingRoom, 1500);
  const debouncedDuration = useDebouncedValue(roomDurationMinutes, 1500);

  const performSave = useCallback(
    async (
      topicToSave: string,
      contentToSave: string,
      dateToSave: string,
      timeToSave: string,
      roomToSave: boolean,
      durationToSave: number,
    ): Promise<boolean> => {
      if (!topicToSave.trim()) return false;
      // Let a running save finish first: it may assign the id and it may
      // already have written exactly these values.
      if (inFlight.current) await inFlight.current;
      if (
        topicToSave === lastSaved.current.topic &&
        contentToSave === lastSaved.current.content &&
        dateToSave === lastSaved.current.occurredAt &&
        timeToSave === lastSaved.current.occurredAtTime &&
        roomToSave === lastSaved.current.hasMeetingRoom &&
        durationToSave === lastSaved.current.roomDurationMinutes
      ) {
        return true;
      }

      setSaveStatus("saving");
      setError(null);

      const run = (async () => {
        try {
          const occurredAt = meetingRoom
            ? localDateTimeToIso(dateToSave, timeToSave)
            : toIsoDate(dateToSave);
          const basePayload: Record<string, unknown> = {
            topic: topicToSave,
            content: contentToSave,
            occurredAt,
          };
          if (meetingRoom) {
            basePayload.hasMeetingRoom = roomToSave;
            basePayload.roomDurationMinutes = durationToSave;
          }
          const saved = savedId.current
            ? await patch<TEntry>(`${endpointBase}/${savedId.current}`, basePayload)
            : await post<TEntry>(endpointBase, basePayload);

          if (!savedId.current) savedId.current = saved.id;
          lastSaved.current = {
            topic: topicToSave,
            content: contentToSave,
            occurredAt: dateToSave,
            occurredAtTime: timeToSave,
            hasMeetingRoom: roomToSave,
            roomDurationMinutes: durationToSave,
          };
          if (meetingRoom) {
            setRoomUrl(saved.roomUrl ?? null);
            setHostRoomUrl(saved.hostRoomUrl ?? null);
          }
          setSaveStatus("saved");
          return true;
        } catch (err: any) {
          setSaveStatus("error");
          // The server rejected this write (e.g. a past meeting, a Whereby
          // failure, or a conflict with a concurrent change) — roll the
          // checkbox back to what the server actually has, so the links and
          // helper text below it don't claim a state that was never saved.
          // Only when this save actually changed the room: a failure for an
          // unrelated reason (e.g. a blank topic) must not undo a tick the
          // user just made.
          const roomChanged = roomToSave !== lastSaved.current.hasMeetingRoom;
          if (meetingRoom && roomChanged) setHasMeetingRoom(lastSaved.current.hasMeetingRoom);
          if (meetingRoom && roomChanged && savedId.current) {
            // The rollback above only fixes the checkbox. A failed room write
            // (notably a 409 from the compare-and-set guard) can mean a
            // concurrent actor already deleted or replaced the room, so
            // roomUrl/hostRoomUrl — only ever set on a successful save above —
            // would otherwise keep pointing at a superseded room until a full
            // reload. Re-read the entry (same endpoint handleFetchTranscription
            // already uses) and resync just the room fields; topic/content/date
            // are untouched, so an in-progress edit there isn't clobbered.
            try {
              const fresh = await get<TEntry>(
                `/internal/client-meetings/single/${savedId.current}`,
              );
              setHasMeetingRoom(!!fresh.hasMeetingRoom);
              setRoomUrl(fresh.roomUrl ?? null);
              setHostRoomUrl(fresh.hostRoomUrl ?? null);
              lastSaved.current.hasMeetingRoom = !!fresh.hasMeetingRoom;
            } catch {
              // Best effort — if the refetch itself fails, the checkbox still
              // matches lastSaved from the rollback above; only the links can
              // remain stale until the next successful save or a reload.
            }
          }
          // Judge by what is on the wire, not by how the editor was opened: an
          // autosave may already have created the entry.
          setError(err?.message || (savedId.current ? t.failedToUpdate : t.failedToCreate));
          return false;
        }
      })();
      inFlight.current = run;
      try {
        return await run;
      } finally {
        if (inFlight.current === run) inFlight.current = null;
      }
    },
    [endpointBase, t, meetingRoom],
  );

  useEffect(() => {
    // Suppress autosave when the user is mid-edit toggling a room ON with a still-past
    // time — backend would throw "meeting room can only be created for a future meeting"
    // and the error would flash before the next autosave overwrites it. Manual Save
    // still goes through and surfaces the message clearly.
    if (
      meetingRoom &&
      debouncedRoom &&
      !entry?.hasMeetingRoom &&
      new Date(`${debouncedDate}T${debouncedTime}:00`).getTime() <= Date.now()
    ) {
      return;
    }
    startTransition(() => {
      performSave(
        debouncedTopic,
        debouncedContent,
        debouncedDate,
        debouncedTime,
        debouncedRoom,
        debouncedDuration,
      );
    });
  }, [
    debouncedTopic,
    debouncedContent,
    debouncedDate,
    debouncedTime,
    debouncedRoom,
    debouncedDuration,
    performSave,
    meetingRoom,
    entry?.hasMeetingRoom,
  ]);

  const handleManualSave = async () => {
    if (!topic.trim()) {
      setError(t.topicRequired);
      return;
    }
    const ok = await performSave(
      topic,
      content,
      occurredAtDate,
      occurredAtTime,
      hasMeetingRoom,
      roomDurationMinutes,
    );
    if (ok) onCancel();
  };

  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [transcriptionLanguage, setTranscriptionLanguage] = useState<string>(() => {
    const stored = entry?.transcriptionLanguage;
    return stored || appLocaleToWherebyCode(userLanguage);
  });
  const [transcriptionText, setTranscriptionText] = useState<string | null>(
    entry?.transcription ?? null,
  );
  const [storedTranscriptionLanguage, setStoredTranscriptionLanguage] = useState<string | null>(
    entry?.transcriptionLanguage ?? null,
  );

  const handleFetchTranscription = async () => {
    if (!savedId.current) return;
    setTranscriptionLoading(true);
    try {
      const res = await post<SyncTranscriptionResponse>(
        `${endpointBase}/${savedId.current}/sync-transcription`,
        {
          language: transcriptionLanguage,
        },
      );

      if (res.status === "ready") {
        // Re-read the meeting to get the freshly-saved transcription.
        const fresh = await get<TEntry>(`/internal/client-meetings/single/${savedId.current}`);
        setTranscriptionText(fresh.transcription ?? null);
        setStoredTranscriptionLanguage(fresh.transcriptionLanguage ?? null);
        setError(null);
      } else {
        setError(res.message);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch transcription");
    } finally {
      setTranscriptionLoading(false);
    }
  };

  useEffect(() => {
    if (saveStatus === "saved") {
      const timer = setTimeout(() => setSaveStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  // Unticking is saved by the debounced autosave and deletes the room in
  // Whereby, so links already shared with the client stop working for good.
  // Ask first whenever a real room exists.
  const handleRoomCheckedChange = async (v: boolean | "indeterminate") => {
    const next = v === true;
    if (!next && (hostRoomUrl || roomUrl)) {
      const confirmed = await confirm({
        title: mt.removeRoomTitle,
        description: mt.removeRoomConfirmation,
        confirmText: mt.removeRoom,
        cancelText: t.cancel,
        variant: "destructive",
      });
      if (!confirmed) return;
    }
    setHasMeetingRoom(next);
  };

  const handleDelete = async () => {
    if (!savedId.current || !onDelete) return;
    const confirmed = await confirm({
      title: t.deleteTitle,
      description: t.deleteConfirmation(topic || entryWord),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (confirmed) onDelete(savedId.current);
  };

  return (
    <>
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t.backToList}
        </Button>

        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label htmlFor="topic" className="sr-only">
                  {t.topic}
                </Label>
                <Input
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={t.enterTopic}
                  className="font-medium text-base bg-transparent px-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 focus:border-solid focus:border-primary"
                />
              </div>
              <div className="w-[160px]">
                <Label htmlFor="occurredAt" className="sr-only">
                  {t.date}
                </Label>
                <DatePicker id="occurredAt" value={occurredAtDate} onChange={setOccurredAtDate} />
              </div>
              {meetingRoom && (
                <div className="w-[140px]">
                  <Label htmlFor="occurredAtTime" className="sr-only">
                    Time
                  </Label>
                  <TimePicker
                    id="occurredAtTime"
                    value={occurredAtTime}
                    onChange={setOccurredAtTime}
                  />
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-[70px] justify-end">
                  {saveStatus === "saving" && (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>{t.saving}</span>
                    </>
                  )}
                  {saveStatus === "saved" && (
                    <>
                      <Check className="h-3 w-3" />
                      <span>{t.saved}</span>
                    </>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualSave}
                  disabled={
                    isPending || !topic.trim() || !hasUnsavedChanges || saveStatus === "saving"
                  }
                >
                  {t.save}
                </Button>

                {savedId.current && onDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="text-destructive hover:text-destructive"
                    disabled={isPending}
                  >
                    {t.delete}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content" className="sr-only">
                {t.content}
              </Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <LexicalMarkdownEditor
                  value={content}
                  onChange={setContent}
                  placeholder={t.writeHere}
                  showSpeechToText={true}
                  showAI={false}
                  className="min-h-[200px]"
                />
              </div>
            </div>

            {meetingRoom && (wherebyEnabled || hasMeetingRoom) && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="hasMeetingRoom"
                    checked={hasMeetingRoom}
                    onCheckedChange={handleRoomCheckedChange}
                    disabled={!isFutureDate}
                  />
                  <Label
                    htmlFor="hasMeetingRoom"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Video className="h-4 w-4" />
                    {mt.meetingRoom}
                  </Label>
                  {!isFutureDate && !hasMeetingRoom && (
                    <span className="text-xs text-muted-foreground">{mt.futureOnly}</span>
                  )}
                </div>

                {isFutureDate && hasMeetingRoom && savedId.current && (hostRoomUrl || roomUrl) && (
                  <span className="pl-6 text-xs text-muted-foreground block">
                    {mt.meetingRoomRemoveHelp}
                  </span>
                )}

                {hasMeetingRoom && (
                  <div className="flex items-center gap-3 pl-6">
                    <Label htmlFor="roomDuration" className="text-sm">
                      {mt.duration}
                    </Label>
                    <Input
                      id="roomDuration"
                      type="number"
                      min={15}
                      max={480}
                      step={15}
                      value={roomDurationMinutes}
                      onChange={(e) =>
                        setRoomDurationMinutes(Math.max(15, Number(e.target.value) || 120))
                      }
                      className="w-24"
                    />
                  </div>
                )}

                {hasMeetingRoom &&
                  savedId.current &&
                  (() => {
                    const storedLang = storedTranscriptionLanguage;
                    const langChanged = !!storedLang && storedLang !== transcriptionLanguage;
                    const buttonLabel = langChanged ? mt.retranscribe : mt.fetchTranscription;
                    return (
                      <div className="pl-6 flex items-center gap-2">
                        <Select
                          value={transcriptionLanguage}
                          onValueChange={setTranscriptionLanguage}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSCRIPTION_LANGUAGES.map((l) => (
                              <SelectItem key={l.code} value={l.code}>
                                {l.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleFetchTranscription}
                          loading={transcriptionLoading}
                        >
                          {buttonLabel}
                        </Button>
                        {storedLang && (
                          <span className="text-xs text-muted-foreground">
                            {mt.storedLanguage}: {storedLang}
                          </span>
                        )}
                      </div>
                    );
                  })()}

                {hasMeetingRoom && (hostRoomUrl || roomUrl) && (
                  <div className="pl-6 space-y-1 text-sm">
                    {hostRoomUrl && (
                      <div>
                        <span className="text-muted-foreground">{mt.hostLink}: </span>
                        <a
                          href={hostRoomUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline break-all"
                        >
                          {hostRoomUrl}
                        </a>
                      </div>
                    )}
                    {roomUrl && (
                      <div>
                        <span className="text-muted-foreground">{mt.guestLink}: </span>
                        <a
                          href={roomUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline break-all"
                        >
                          {roomUrl}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {meetingRoom && transcriptionText?.trim() && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>{mt.transcriptionTitle}</span>
                  {storedTranscriptionLanguage && (
                    <span className="text-xs text-muted-foreground font-normal">
                      ({storedTranscriptionLanguage})
                    </span>
                  )}
                </div>
                <div className="rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap max-h-[300px] overflow-auto">
                  {transcriptionText.trim()}
                </div>
              </div>
            )}

            {entry && (
              <div className="text-xs text-muted-foreground">
                {entry.createdBy?.name} &bull;{" "}
                <TimeAgoTooltip date={parseDbTimestamp(entry.updatedAt)}>
                  {getRelativeTime(entry.updatedAt, userLanguage)}
                </TimeAgoTooltip>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationDialog />
    </>
  );
}
